import React, { useEffect, useRef, useState } from "react";
import MessageList from "./MessageList"; // Assuming MessageList is in the same directory
import { ChatInput } from "./ChatInput"; // Assuming ChatInput is in the same directory
import { useUIStore } from "../../store/uiStore";
// No longer need useChatStore here for setChatViewMode
// import { useChatStore } from '../../store/chatStore';
import styles from "./ChatSidebarView.module.css"; // Import the CSS module
import WidgetHeader from "../common/WidgetHeader"; // Import the new WidgetHeader
// import { ExpandIcon, MinimizeIcon, CloseIcon, MenuIcon } from '../common/Icons'; // Import icons
// import { useUIStore } from '../../store/uiStore'; // Placeholder for UI store
import useAuthStore from "../../store/authStore"; // Removed UserStatus import
import LoadingIndicator from "../common/LoadingIndicator"; // Import LoadingIndicator
import ConversationHistory from "./ConversationHistory"; // <--- Import ConversationHistory
import useMediaQuery from "../../hooks/useMediaQuery"; // <--- Import useMediaQuery
import { logDebug } from "../../utils/logging"; // Import logging utility
import { trackEvent } from "../../services/analyticsService";
import { useChatStore } from "../../store/chatStore";
import BusinessProfileModal from "../common/BusinessProfileModal";

// No props needed for this component currently
// interface ChatSidebarViewProps {
//   // Props like isVisible, onClose, onExpand, etc.
// }

const ChatSidebarView: React.FC = () => {
  // Emit event when a chat view is opened inside fullpage shell
  const { route, params } = useUIStore();
  useEffect(() => {
    if (route === "chat") {
      trackEvent("fullpage_session_opened", {
        route,
        session_id: params?.sessionId,
      });
    }
  }, [route, params?.sessionId]);
  // Removed ChatSidebarViewProps
  // Get actions and state from the new uiStore
  const {
    chatViewMode, // Need to read the current view mode
    isHistoryPanelVisibleOnMobile,
    closeChatToBubble,
    openChatToFullscreen,
    minimizeChatToSidebar,
    toggleHistoryPanelVisibility,
    isInitializingChat, // Get the new state from uiStore
  } = useUIStore();

  const { userStatus, messageLimits, apiUrls, isLoadingInitialData } =
    useAuthStore();

  // Chat actions
  const startNewChatSession = useChatStore((s) => s.startNewChatSession);

  // Remove setChatViewMode from chatStore
  // const setChatViewMode = useChatStore((state) => state.setChatViewMode);

  const handleClose = () => {
    logDebug("Close button clicked. Closing to bubble view via uiStore.");
    closeChatToBubble();
  };

  const handleExpandOrMinimize = () => {
    logDebug(
      `Expand/Minimize button clicked. Currently chatViewMode: ${chatViewMode}. Toggling via uiStore.`,
    );
    if (chatViewMode === "fullscreen") {
      minimizeChatToSidebar();
    } else if (chatViewMode === "sidebar") {
      openChatToFullscreen();
    }
  };

  const handleToggleHistoryPanel = () => {
    // On fullpage mobile we want the hamburger to open the AppShell sidebar, not the history overlay
    const isInFullpageShell =
      typeof document !== "undefined" && !!document.getElementById("smartie-fullpage-root");
    if (isInFullpageShell && isMobile) {
      logDebug("Opening fullpage sidebar from chat header hamburger");
      useUIStore.getState().toggleSidebarMobile();
      return;
    }
    logDebug(
      "Toggling history panel visibility on mobile (widget sidebar mode)",
    );
    toggleHistoryPanelVisibility();
  };

  // Placeholder data for UI elements - will be replaced by Zustand state
  // To test different states, change userStatusPlaceholder and messagesRemainingPlaceholder:
  // 1. Guest, messages available: userStatusPlaceholder = 'guest', messagesRemainingPlaceholder = 5
  // 2. Guest, limit reached: userStatusPlaceholder = 'guest', messagesRemainingPlaceholder = 0
  // 3. Basic user, messages available: userStatusPlaceholder = 'loggedin_basic', messagesRemainingPlaceholder = 5
  // 4. Basic user, limit reached: userStatusPlaceholder = 'loggedin_basic', messagesRemainingPlaceholder = 0
  // 5. Premium user: userStatusPlaceholder = 'loggedin_premium', messagesRemainingPlaceholder = 50 (limit not shown for premium)

  const isGuestLimitReached =
    userStatus === "guest" &&
    messageLimits?.max !== null &&
    messageLimits?.max !== undefined &&
    messageLimits?.current !== null &&
    messageLimits?.current >= messageLimits.max;
  const isBasicUserLimitReached =
    userStatus === "loggedin_basic" &&
    messageLimits?.max !== null &&
    messageLimits?.max !== undefined &&
    messageLimits?.current !== null &&
    messageLimits?.current >= messageLimits.max;

  const loginUrl = apiUrls?.loginUrl || "/wp-login.php"; // Default fallback
  const upgradeUrl = apiUrls?.upgradeUrl || "/grupuri/plus/"; // Default fallback

  // Use isLoadingInitialData from authStore as the primary signal for the initial data fetch,
  // isInitializingChat from uiStore can be a more general flag if needed for other UI initializing states.
  // For this specific purpose, isLoadingInitialData is more direct from the data fetching process itself.
  const showLoadingState = isLoadingInitialData || isInitializingChat;

  const isMobile = useMediaQuery("(max-width: 768px)"); // <--- Add isMobile check

  const handleHeaderNewChat = () => {
    const isInFullpageShell =
      typeof document !== "undefined" &&
      !!document.getElementById("smartie-fullpage-root");

    // Create new session
    startNewChatSession();

    // Close overlays if open (history overlay on mobile)
    if (isMobile && isHistoryPanelVisibleOnMobile) {
      toggleHistoryPanelVisibility();
    }

    // Navigate to welcome
    useUIStore.getState().navigate("welcome");

    // Focus chat input
    setTimeout(() => {
      const input: HTMLInputElement | null = document.querySelector(
        'input[aria-label="Câmp pentru mesaj chat"]',
      );
      input?.focus();
    }, 0);

    // Analytics
    const mode = isInFullpageShell
      ? "fullpage"
      : chatViewMode === "fullscreen"
        ? "expanded"
        : "narrow";
    // Reuse existing naming pattern: treat as generic quick action toward welcome
    trackEvent("welcome_screen_viewed", { mode, source: "header_new_chat" });
  };

  // Business Profile modal state and focus management
  const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
  const businessProfileTriggerRef = useRef<HTMLElement | null>(null);

  const handleOpenBusinessProfile = () => {
    // Save current focused element to restore later
    businessProfileTriggerRef.current = (document.activeElement as HTMLElement) || null;
    setIsBusinessProfileOpen(true);
    // Note: event 'business_profile_modal_opened' is emitted inside BusinessProfileModal on open
  };

  const handleCloseBusinessProfile = () => {
    setIsBusinessProfileOpen(false);
    // Restore focus to the header trigger button
    setTimeout(() => businessProfileTriggerRef.current?.focus(), 0);
  };

  return (
    <div className={styles.sidebarViewContainer}>
      {(() => {
        // Detect if we are rendered inside the fullpage shell (AppShell)
        // Note: AppShell is mounted inside a ShadowRoot attached to #smartie-fullpage-root,
        // so we detect the host element, not the internal shell container.
        const isInFullpageShell =
          typeof document !== "undefined" &&
          !!document.getElementById("smartie-fullpage-root");

        // Flags per FP‑1.13
        const showClose = !isInFullpageShell;
        const showExpandMinimize = !isInFullpageShell;
        const showHistory = isInFullpageShell ? isMobile : true; // Fullpage: history toggle only on mobile
        const showNewChat = !isInFullpageShell; // Avoid duplicates in fullpage; canonical action lives in sidebar
        const showBusinessProfile = !isInFullpageShell; // Same rationale as above

        return (
          <WidgetHeader
            brandText="Smartie"
            chatViewMode={isInFullpageShell ? "sidebar" : chatViewMode}
            isHistoryPanelVisibleOnMobile={isHistoryPanelVisibleOnMobile}
            onClose={handleClose}
            onExpandOrMinimize={handleExpandOrMinimize}
            onToggleHistoryPanel={handleToggleHistoryPanel}
            showHistory={showHistory}
            showNewChat={showNewChat}
            showBusinessProfile={showBusinessProfile}
            showExpandMinimize={showExpandMinimize}
            showClose={showClose}
            useOverflowOnNarrow
            onNewChat={showNewChat ? handleHeaderNewChat : undefined}
            onOpenBusinessProfile={showBusinessProfile ? handleOpenBusinessProfile : undefined}
          />
        );
      })()}

      {/* Conditional rendering for ConversationHistory as overlay on mobile sidebar */}
      {isMobile &&
        chatViewMode === "sidebar" &&
        isHistoryPanelVisibleOnMobile && (
          <ConversationHistory overlayMode="sidebarMobile" />
        )}

      {showLoadingState ? (
        <div className={styles.loadingContainer}>
          <LoadingIndicator text="Se inițializează..." />
        </div>
      ) : (
        <>
          <div className={styles.conversationArea}>
            {/* Initial Greeting - conditionally rendered if no messages or based on a flag */}
            {/* For now, MessageList will handle empty state if applicable */}
            {/* Consider adding an explicit greeting if MessageList is empty later */}
            {/* <p style={{ textAlign: 'center', color: '#666', marginTop: '20px' }}>Salutări, sunt Smartie...</p> */}
            <MessageList />
          </div>

          {isGuestLimitReached ? (
            <div className={styles.limitReachedMessage}>
              <p>Autentifică-te pentru a putea trimite mai multe mesaje.</p>
              <a href={loginUrl} className={styles.actionButton}>
                Autentificare
              </a>
            </div>
          ) : isBasicUserLimitReached ? (
            <div className={styles.limitReachedMessage}>
              <p>
                Devino membru SMARTERS+ pentru a continua conversația și pentru
                funcționalități mai puternice.
              </p>
              <a href={upgradeUrl} className={styles.actionButton}>
                Devino SMARTERS+
              </a>
            </div>
          ) : (
            // This div acts as a container for ChatInput and the footer, ensuring footer is below input
            <div className={styles.inputAndFooterContainer}>
              <ChatInput />
              {/* Business Profile Modal from header trigger */}
              <BusinessProfileModal
                isOpen={isBusinessProfileOpen}
                onClose={handleCloseBusinessProfile}
              />
              <div className={styles.feedbackContainer}>
                <a
                  href="https://smarters.ro/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.feedbackLink}
                  onClick={() => trackEvent("feedback_link_click")}
                >
                  Lasă feedback
                </a>
              </div>
              {/* {userStatus !== 'loggedin_premium' && messageLimits && (
                <footer className={styles.footerInfo}>
                  <span>{messageLimits.current}/{messageLimits.max ?? '∞'} mesaje rămase</span> | <span>Status: {getUserStatusDisplay(userStatus)}</span>
                </footer>
              )} */}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatSidebarView;
